import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Building2, Download, CheckCircle, XCircle, Users, DollarSign, Briefcase, Info } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface CorkClient {
    ID: number;
    Code: string;
    PersonalName: string;
    FileAs: string;
    ClientType: string;
    FirstName: string;
    LastName: string;
    Email1: string | null;
    Email2: string | null;
    Phone1: string | null;
    Phone2: string | null;
    Mobile1: string | null;
    YearEnd: string | null;
    TakeOnDate: string | null;
    CreatedOn: string | null;
    FeesBalance: number | null;
    WIPBalance: number | null;
    Turnover: number | null;
    CurrentBal: number | null;
    Aged30Days: number | null;
    Aged60Days: number | null;
    Aged90Days: number | null;
    Aged120Days: number | null;
    AgedBalance: number | null;
    Unallocated: number | null;
    LastBillAmount: number | null;
    LastBillDate: string | null;
    LastReceiptAmount: number | null;
    LastReceiptDate: string | null;
    FeesBudget: number | null;
    WIPBudget: number | null;
    CategoriesDesc: string | null;
    ServicesUsedDesc: string | null;
    DateOfBirth: string | null;
    RegNo_NI_LLPRegNo_UTR: string | null;
    VATNumber: string | null;
    IncorporationDate: string | null;
    PrincActivity: string | null;
    Status: string;
    EntityType: string;
    Office: string | null;
}

const formatCurrency = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return "—";
    return `€${val.toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val: string | null | undefined): string => {
    if (!val) return "—";
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("en-IE");
    } catch {
        return "—";
    }
};

const CorkClients: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [clients, setClients] = useState<CorkClient[]>([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [entityFilter, setEntityFilter] = useState<string>("all");
    const [officeFilter, setOfficeFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (accounts.length === 0) return;
            setLoading(true);
            setError(null);
            try {
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) { setLoading(false); return; }
                const response = await fetch(`${getApiBaseUrl()}/api/cork-clients`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.details || errData.error || `HTTP ${response.status}`);
                }
                const data: CorkClient[] = await response.json();
                // Trim whitespace from string fields
                const cleaned = data.map((c) => ({
                    ...c,
                    PersonalName: c.PersonalName?.trim() || "",
                    FileAs: c.FileAs?.trim() || "",
                    Email1: c.Email1?.trim() || null,
                    Phone1: c.Phone1?.trim() || null,
                    Mobile1: c.Mobile1?.trim() || null,
                    Office: c.Office?.trim() || null,
                    CategoriesDesc: c.CategoriesDesc?.trim() || null,
                    PrincActivity: c.PrincActivity?.trim() || null,
                    VATNumber: c.VATNumber?.trim() || null,
                }));
                setClients(cleaned);
            } catch (err: any) {
                console.error("Failed to fetch Cork clients:", err);
                setError(err.message || "Failed to fetch data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [accounts, instance, setLoading]);

    // Compute filter options
    const entityTypes = [...new Set(clients.map((c) => c.EntityType).filter(Boolean))].sort();
    const offices = [...new Set(clients.map((c) => c.Office).filter(Boolean))].sort() as string[];
    const statuses = [...new Set(clients.map((c) => c.Status).filter(Boolean))].sort();

    // Filtered data
    const filtered = clients.filter((c) => {
        if (statusFilter !== "all" && c.Status !== statusFilter) return false;
        if (entityFilter !== "all" && c.EntityType !== entityFilter) return false;
        if (officeFilter !== "all" && c.Office !== officeFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                (c.FileAs || "").toLowerCase().includes(q) ||
                (c.Code || "").toLowerCase().includes(q) ||
                (c.Email1 || "").toLowerCase().includes(q) ||
                (c.Phone1 || "").toLowerCase().includes(q) ||
                (c.EntityType || "").toLowerCase().includes(q) ||
                (c.Office || "").toLowerCase().includes(q) ||
                (c.PrincActivity || "").toLowerCase().includes(q)
            );
        }
        return true;
    });

    // Stats
    const activeCount = clients.filter((c) => c.Status === "Active").length;
    const closedCount = clients.filter((c) => c.Status === "Closed").length;
    const totalFees = clients.reduce((sum, c) => sum + (c.FeesBalance || 0), 0);
    const totalWIP = clients.reduce((sum, c) => sum + (c.WIPBalance || 0), 0);
    const totalTurnover = clients.reduce((sum, c) => sum + (c.Turnover || 0), 0);

    const columns: TableColumn<CorkClient>[] = [
        {
            name: "Code",
            selector: (row) => row.Code || "",
            sortable: true,
            width: "80px",
        },
        {
            name: "Client Name",
            selector: (row) => row.FileAs || "",
            sortable: true,
            cell: (row) => (
                <div style={{ fontWeight: 500 }}>
                    {row.FileAs || row.PersonalName || "—"}
                </div>
            ),
            grow: 2,
        },
        {
            name: "Status",
            selector: (row) => row.Status || "",
            sortable: true,
            cell: (row) => (
                <span
                    className={`status-badge ${row.Status === "Active" ? "status-active" : "status-inactive"}`}
                >
                    {row.Status || "—"}
                </span>
            ),
            width: "100px",
            center: true,
        },
        {
            name: "Entity Type",
            selector: (row) => row.EntityType || "",
            sortable: true,
            width: "180px",
        },
        {
            name: "Office",
            selector: (row) => row.Office || "",
            sortable: true,
            width: "150px",
            cell: (row) => <span>{row.Office || "—"}</span>,
        },
        {
            name: "Email",
            selector: (row) => row.Email1 || "",
            sortable: true,
            cell: (row) =>
                row.Email1 ? (
                    <a href={`mailto:${row.Email1}`} style={{ color: "var(--accent-blue)" }}>
                        {row.Email1}
                    </a>
                ) : (
                    <span style={{ color: "var(--text-muted)" }}>—</span>
                ),
            width: "200px",
        },
        {
            name: "Phone",
            selector: (row) => row.Phone1 || "",
            sortable: true,
            width: "130px",
            cell: (row) => <span>{row.Phone1 || "—"}</span>,
        },
        {
            name: "Year End",
            selector: (row) => row.YearEnd || "",
            sortable: true,
            cell: (row) => <span>{formatDate(row.YearEnd)}</span>,
            width: "110px",
        },
        {
            name: "Fees Balance",
            selector: (row) => row.FeesBalance || 0,
            sortable: true,
            cell: (row) => (
                <span style={{ fontVariantNumeric: "tabular-nums", color: (row.FeesBalance || 0) < 0 ? "var(--error-color, #dc2626)" : undefined }}>
                    {formatCurrency(row.FeesBalance)}
                </span>
            ),
            right: true,
            width: "120px",
        },
        {
            name: "WIP Balance",
            selector: (row) => row.WIPBalance || 0,
            sortable: true,
            cell: (row) => (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(row.WIPBalance)}
                </span>
            ),
            right: true,
            width: "120px",
        },
        {
            name: "Turnover",
            selector: (row) => row.Turnover || 0,
            sortable: true,
            cell: (row) => (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCurrency(row.Turnover)}
                </span>
            ),
            right: true,
            width: "120px",
        },
        {
            name: "Taken On",
            selector: (row) => row.TakeOnDate || row.CreatedOn || "",
            sortable: true,
            cell: (row) => <span>{formatDate(row.TakeOnDate || row.CreatedOn)}</span>,
            width: "110px",
        },
    ];

    const exportCSV = () => {
        if (filtered.length === 0) return;
        const headers = [
            "Code", "Client Name", "Status", "Entity Type", "Office", "Email", "Phone", "Mobile",
            "Year End", "Take On Date", "Fees Balance", "WIP Balance", "Turnover",
            "Current Balance", "30 Days", "60 Days", "90 Days", "120 Days",
            "Last Bill Amount", "Last Bill Date", "Last Receipt Amount", "Last Receipt Date",
            "Categories", "Principal Activity", "VAT Number", "Reg Number"
        ];
        const rows = filtered.map((c) => [
            c.Code, c.FileAs, c.Status, c.EntityType, c.Office || "",
            c.Email1 || "", c.Phone1 || "", c.Mobile1 || "",
            formatDate(c.YearEnd), formatDate(c.TakeOnDate),
            c.FeesBalance ?? "", c.WIPBalance ?? "", c.Turnover ?? "",
            c.CurrentBal ?? "", c.Aged30Days ?? "", c.Aged60Days ?? "", c.Aged90Days ?? "", c.Aged120Days ?? "",
            c.LastBillAmount ?? "", formatDate(c.LastBillDate),
            c.LastReceiptAmount ?? "", formatDate(c.LastReceiptDate),
            c.CategoriesDesc || "", c.PrincActivity || "", c.VATNumber || "",
            c.RegNo_NI_LLPRegNo_UTR || ""
        ]);
        const csvContent = [
            headers.join(","),
            ...rows.map((row) =>
                row.map((val) => {
                    const str = String(val).replace(/"/g, '""');
                    return str.includes(",") || str.includes('"') || str.includes("\n")
                        ? `"${str}"`
                        : str;
                }).join(",")
            ),
        ].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cork-clients-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Building2 size={20} />
                    <h1>Cork Clients</h1>
                </div>
            </div>

            {/* Stats */}
            <div className="stats-grid stats-grid-6">
                <div className="stat-card">
                    <div className="stat-icon"><Users size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{clients.length}</div>
                        <div className="stat-label">Total Clients</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><CheckCircle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{activeCount}</div>
                        <div className="stat-label">Active</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><XCircle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{closedCount}</div>
                        <div className="stat-label">Closed</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon"><DollarSign size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalTurnover)}</div>
                        <div className="stat-label">Total Turnover</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><Briefcase size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalWIP)}</div>
                        <div className="stat-label">Total WIP</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><DollarSign size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalFees)}</div>
                        <div className="stat-label">Total Fees Balance</div>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{
                    background: "var(--error-bg, #fef2f2)",
                    color: "var(--error-color, #dc2626)",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 16,
                    border: "1px solid var(--error-border, #fecaca)"
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Controls */}
            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Statuses</option>
                    {statuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                <select
                    value={entityFilter}
                    onChange={(e) => setEntityFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Entity Types</option>
                    {entityTypes.map((et) => (
                        <option key={et} value={et}>{et}</option>
                    ))}
                </select>
                {offices.length > 1 && (
                    <select
                        value={officeFilter}
                        onChange={(e) => setOfficeFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Offices</option>
                        {offices.map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                )}
                <button className="download-btn" onClick={exportCSV} disabled={filtered.length === 0} title="Download as CSV">
                    <Download size={16} />
                    Export CSV
                </button>
            </div>

            {/* Data Table */}
            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filtered}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    defaultSortFieldId={2}
                    noDataComponent={
                        <div className="no-data">
                            {error ? "Failed to load data" : "No clients found"}
                        </div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '8px',
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-muted)',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottomColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                            },
                        },
                        headCells: {
                            style: {
                                color: 'var(--text-secondary)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: 'var(--bg-hover)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                borderTopColor: 'var(--border-color)',
                            },
                            pageButtonsStyle: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:disabled': {
                                    opacity: 0.4,
                                },
                            },
                        },
                    }}
                />
            </div>

            {/* Data Source Footer */}
            <div
                style={{
                    marginTop: 24,
                    padding: '16px 20px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 600, color: 'var(--text-primary)' }}>
                    <Info size={16} />
                    <span>Data Sources</span>
                    <span
                        style={{
                            marginLeft: 8,
                            padding: '2px 8px',
                            background: 'var(--accent-red, #dc2626)',
                            color: '#fff',
                            borderRadius: 4,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                        }}
                    >
                        PRODUCTION DATA
                    </span>
                </div>
                <p style={{ margin: '0 0 8px 0' }}>
                    Cork client records come live from the Cork practice-management SQL Server. Status, entity type, office, fees/WIP balances, ageing, turnover and last bill/receipt activity are read directly from the source database.
                </p>
                <details>
                    <summary style={{ cursor: 'pointer', color: 'var(--accent-blue)', userSelect: 'none' }}>
                        Show technical details
                    </summary>
                    <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--bg-primary)', borderRadius: 6, border: '1px solid var(--border-color)' }}>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--text-primary)' }}>Live API</p>
                        <ul style={{ margin: '0 0 12px 18px', padding: 0 }}>
                            <li><code>GET /api/cork-clients</code> &mdash; direct query against the Cork production SQL Server. <strong>Source: Cork practice-management database.</strong></li>
                        </ul>
                        <p style={{ margin: '0 0 10px 0', fontWeight: 600, color: 'var(--text-primary)' }}>Tables read</p>
                        <ul style={{ margin: '0 0 0 18px', padding: 0 }}>
                            <li><code>Client</code> &mdash; core client record (code, name, contact details, year-end, take-on date, balances, ageing buckets, turnover, last bill/receipt, fees &amp; WIP budgets, services used, registration / VAT numbers, incorporation date, principal activity).</li>
                            <li><code>ClientStatus</code> &mdash; status description (Active / Closed / etc.).</li>
                            <li><code>EntityType</code> &mdash; entity type description.</li>
                            <li><code>Office</code> &mdash; office description.</li>
                        </ul>
                    </div>
                </details>
            </div>
        </div>
    );
};

export default CorkClients;
